import { useState, useEffect, useCallback } from 'react'
import { PageHeader, StatusBadge, colors, btnPrimary } from '../../components/ui'
import { getAuthToken } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'
import { type UserRole, roleSatisfies } from '@alpha-edge/shared'

type BackupTarget = 's3' | 'azure_blob' | 'gcs' | ''

interface BackupConfig {
  readonly target: BackupTarget
  readonly bucket: string
  readonly prefix: string
}

interface RetentionCounts {
  readonly hourly: number
  readonly daily: number
  readonly weekly: number
  readonly monthly: number
  readonly yearly: number
}

interface BackupStatus {
  readonly lastSuccessfulAt: string | null
  readonly lastTargetBytes: number | null
  readonly nextScheduled: string | null
  readonly lastError?: string | null
  readonly retentionCounts: RetentionCounts
}

const TARGET_LABELS: Record<BackupTarget, string> = {
  '': 'Not configured',
  s3: 'AWS S3',
  azure_blob: 'Azure Blob Storage',
  gcs: 'Google Cloud Storage',
}

function formatDateTime(iso: string | null): string {
  if (!iso) return 'Never'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—'
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  return { Authorization: `Bearer ${getAuthToken()}`, ...extra }
}

export function OffsiteBackupPage() {
  const { user } = useAuth()
  const isCentralAdmin = roleSatisfies((user?.role as UserRole) ?? 'viewer', ['medflow_admin'])
  const [config, setConfig] = useState<BackupConfig>({ target: '', bucket: '', prefix: '' })
  const [credentialStored, setCredentialStored] = useState(false)
  const [credentialInput, setCredentialInput] = useState('')
  const [status, setStatus] = useState<BackupStatus | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null)
  const [storingCredential, setStoringCredential] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true)
    try {
      const res = await fetch('/api/meta/backup-status', {
        headers: authHeaders(),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `Request failed (${res.status})`)
      }
      const body = (await res.json()) as { data: BackupStatus }
      setStatus(body.data)
    } catch {
      // Status load failure is non-fatal; show the config form regardless
      setStatus(null)
    } finally {
      setLoadingStatus(false)
    }
  }, [])

  // Also fetch current config from system_config on mount by reading backup-status
  // and separately the config endpoint (config is served via the status endpoint for now).
  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const handleSaveConfig = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await fetch('/api/admin/offsite-backup/config', {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          target: config.target,
          bucket: config.bucket,
          prefix: config.prefix,
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `Request failed (${res.status})`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }, [config])

  const handleStoreCredential = useCallback(async () => {
    if (!credentialInput.trim()) return
    setStoringCredential(true)
    setError(null)
    try {
      const secretKey = `offsite_backup.${config.target || 'generic'}.credential`
      // Try PUT first (update if exists), fall back to POST (create).
      const putRes = await fetch(`/api/tenant-secrets/${encodeURIComponent(secretKey)}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ value: credentialInput, description: 'Offsite backup cloud credentials' }),
      })
      if (!putRes.ok) {
        // If 404, create instead.
        const postRes = await fetch('/api/tenant-secrets', {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            secretKey,
            value: credentialInput,
            description: 'Offsite backup cloud credentials',
          }),
        })
        if (!postRes.ok) {
          const body = (await postRes.json().catch(() => ({}))) as { error?: string }
          throw new Error(body.error ?? `Failed to store credential (${postRes.status})`)
        }
      }
      // Persist the credentialRef so backup.sh knows where to look.
      await fetch('/api/admin/offsite-backup/config', {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ credentialRef: secretKey }),
      })
      setCredentialStored(true)
      setCredentialInput('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to store credential')
    } finally {
      setStoringCredential(false)
    }
  }, [credentialInput, config.target])

  const handleTestConnection = useCallback(async () => {
    setTesting(true)
    setTestResult(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/offsite-backup/test', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({}),
      })
      const body = (await res.json()) as { data?: { ok: boolean; error?: string }; error?: string }
      if (!res.ok) {
        setTestResult({ ok: false, error: body.error ?? `Request failed (${res.status})` })
      } else {
        setTestResult(body.data ?? { ok: false, error: 'Unexpected response' })
      }
    } catch (err) {
      setTestResult({ ok: false, error: err instanceof Error ? err.message : 'Connection test failed' })
    } finally {
      setTesting(false)
    }
  }, [])

  return (
    <div style={{ padding: '24px', maxWidth: '720px' }}>
      <PageHeader title={isCentralAdmin ? 'Backup Configuration' : 'Off-Site Backup'} />

      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '4px',
            color: '#dc2626',
            padding: '10px 14px',
            marginBottom: '16px',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Configuration card */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '20px 24px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: '0.9375rem', color: colors.text }}>Cloud Storage Target</h3>

        <p
          style={{
            color: colors.textMuted,
            marginTop: 0,
            marginBottom: '16px',
            lineHeight: '1.6',
            fontSize: '0.875rem',
          }}
        >
          {isCentralAdmin
            ? 'Configure the cloud storage target for Central database backups. Credentials are stored in the encrypted vault — never in plain config.'
            : 'Configure a BYO cloud bucket to receive an off-site copy of every local backup. Credentials are stored in the encrypted tenant-secret vault — never in plain config. You own the bucket and the BAA obligations.'}
        </p>

        {/* Target dropdown */}
        <div style={{ marginBottom: '14px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.text,
              marginBottom: '6px',
            }}
          >
            Storage provider
          </label>
          <select
            value={config.target}
            onChange={(e) => setConfig((c) => ({ ...c, target: e.target.value as BackupTarget }))}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              fontSize: '0.875rem',
              color: colors.text,
              background: '#fff',
            }}
          >
            {(Object.entries(TARGET_LABELS) as [BackupTarget, string][]).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Bucket */}
        <div style={{ marginBottom: '14px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.text,
              marginBottom: '6px',
            }}
          >
            Bucket name
          </label>
          <input
            type="text"
            value={config.bucket}
            onChange={(e) => setConfig((c) => ({ ...c, bucket: e.target.value }))}
            placeholder="my-backup-bucket"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              fontSize: '0.875rem',
              color: colors.text,
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Prefix */}
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.text,
              marginBottom: '6px',
            }}
          >
            Key prefix <span style={{ fontWeight: 400, color: colors.textMuted }}>(optional)</span>
          </label>
          <input
            type="text"
            value={config.prefix}
            onChange={(e) => setConfig((c) => ({ ...c, prefix: e.target.value }))}
            placeholder="alpha-edge/backups"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              fontSize: '0.875rem',
              color: colors.text,
              boxSizing: 'border-box',
            }}
          />
        </div>

        <button onClick={handleSaveConfig} disabled={saving} style={btnPrimary}>
          {saving ? 'Saving…' : 'Save configuration'}
        </button>
      </div>

      {/* Credential card */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '20px 24px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: '0.9375rem', color: colors.text }}>Cloud Credentials</h3>
        <p
          style={{
            color: colors.textMuted,
            marginTop: 0,
            marginBottom: '16px',
            lineHeight: '1.6',
            fontSize: '0.875rem',
          }}
        >
          Paste your credentials below (JSON service account key, access key pair as <code>KEY_ID:SECRET</code>, etc.).
          They are encrypted at rest and never echoed back.
        </p>

        {credentialStored && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '14px',
            }}
          >
            <StatusBadge status="active" label="Credential stored" />
            <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>Upload a new value below to rotate.</span>
          </div>
        )}

        <textarea
          value={credentialInput}
          onChange={(e) => setCredentialInput(e.target.value)}
          placeholder="Paste credentials here…"
          rows={4}
          style={{
            width: '100%',
            padding: '8px 10px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontFamily: 'monospace',
            color: colors.text,
            resize: 'vertical',
            boxSizing: 'border-box',
            marginBottom: '12px',
          }}
        />

        <button
          onClick={handleStoreCredential}
          disabled={storingCredential || !credentialInput.trim()}
          style={btnPrimary}
        >
          {storingCredential ? 'Storing…' : 'Store credential'}
        </button>
      </div>

      {/* Connection test card */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '20px 24px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: '0.9375rem', color: colors.text }}>Connection Test</h3>
        <p
          style={{
            color: colors.textMuted,
            marginTop: 0,
            marginBottom: '14px',
            lineHeight: '1.6',
            fontSize: '0.875rem',
          }}
        >
          Verify the bucket is reachable and credentials are valid before the next scheduled backup.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button onClick={handleTestConnection} disabled={testing || config.target === ''} style={btnPrimary}>
            {testing ? 'Testing…' : 'Test connection'}
          </button>

          {testResult !== null && (
            <span style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <StatusBadge status={testResult.ok ? 'active' : 'error'} label={testResult.ok ? 'Connected' : 'Failed'} />
              {!testResult.ok && testResult.error && <span style={{ color: '#dc2626' }}>{testResult.error}</span>}
            </span>
          )}
        </div>
      </div>

      {/* Retention / status card */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '20px 24px',
        }}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: '0.9375rem', color: colors.text }}>Backup Status</h3>

        {loadingStatus ? (
          <span style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading…</span>
        ) : status ? (
          <>
            {status.lastError && (
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '4px',
                  color: '#dc2626',
                  padding: '10px 14px',
                  marginBottom: '16px',
                  fontSize: '0.875rem',
                }}
              >
                Last error: {status.lastError}
              </div>
            )}
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '8px 24px',
                margin: '0 0 20px',
                fontSize: '0.875rem',
              }}
            >
              <dt style={{ color: colors.textMuted, fontWeight: 600 }}>Last successful backup</dt>
              <dd style={{ color: colors.text, margin: 0 }}>{formatDateTime(status.lastSuccessfulAt)}</dd>
              <dt style={{ color: colors.textMuted, fontWeight: 600 }}>Last backup size</dt>
              <dd style={{ color: colors.text, margin: 0 }}>{formatBytes(status.lastTargetBytes)}</dd>
              <dt style={{ color: colors.textMuted, fontWeight: 600 }}>Next scheduled</dt>
              <dd style={{ color: colors.text, margin: 0 }}>{formatDateTime(status.nextScheduled)}</dd>
            </dl>

            <h4 style={{ margin: '0 0 10px', fontSize: '0.875rem', color: colors.text }}>
              Retention counts (off-site)
            </h4>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, auto)',
                gap: '4px 20px',
                margin: 0,
                fontSize: '0.875rem',
              }}
            >
              {(['hourly', 'daily', 'weekly', 'monthly', 'yearly'] as const).map((tier) => (
                <div key={tier} style={{ textAlign: 'center' }}>
                  <dt style={{ color: colors.textMuted, fontWeight: 600, textTransform: 'capitalize' }}>{tier}</dt>
                  <dd style={{ color: colors.text, margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                    {status.retentionCounts[tier]}
                  </dd>
                </div>
              ))}
            </dl>
          </>
        ) : (
          <span style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
            No backup status recorded yet. Run a backup with <code>backup.sh --offsite</code> to populate this panel.
          </span>
        )}
      </div>
    </div>
  )
}
